import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  LITTER_DISPLAY_STATES,
  LITTER_STATES
} from 'dpl/shared/constants/shared';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { littersResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { nameUnnamedPuppies } from 'dpl/shared/utils/breeder_app';
import { BREED_ID } from 'dpl/constants/query_params';

export default function useLitters(params: Record<string, any> = {}) {
  const { breederProfileId } = useSelector(({ server }: any) => server);
  const searchBreedId = useSelector(({ queryParams }: any) =>
    Number(queryParams[BREED_ID])
  );

  const {
    isFetching: isFetchingCurrentLitters,
    value: { data: currentLitters = [] }
  } = useResourceQuery(
    littersResourceDefinition(breederProfileId, {
      status: LITTER_DISPLAY_STATES.CURRENT,
      ...params
    })
  );

  const {
    isFetching: isFetchingPastLitters,
    value: { data: pastLitters = [] }
  } = useResourceQuery(
    littersResourceDefinition(breederProfileId, {
      status: LITTER_DISPLAY_STATES.PAST,
      exclude_adult: true,
      ...params
    })
  );

  const { availablePuppies, reservedPuppies, availableAdultDogs } =
    useMemo(() => {
      return currentLitters.reduce(
        (acc: any, litter: any) => {
          const puppies = nameUnnamedPuppies(litter.puppies);

          puppies.forEach((puppy: any) => {
            if (puppy.reserved || puppy.snoozed) {
              acc.reservedPuppies.push(puppy);
            } else if (puppy.adult) {
              acc.availableAdultDogs.push(puppy);
            } else {
              acc.availablePuppies.push(puppy);
            }
          });

          return acc;
        },
        { availablePuppies: [], reservedPuppies: [], availableAdultDogs: [] }
      );
    }, [currentLitters]);

  const sortedAvailablePuppies = useMemo(() => {
    return [...availablePuppies].sort((a: any, b: any) => {
      if (searchBreedId) {
        const aBreedMatch = a.breed_id === searchBreedId;
        const bBreedMatch = b.breed_id === searchBreedId;

        if (aBreedMatch && !bBreedMatch) return -1;
        if (!aBreedMatch && bBreedMatch) return 1;
      }
      return (
        new Date(b.birth_date).getTime() - new Date(a.birth_date).getTime()
      );
    });
  }, [availablePuppies, searchBreedId]);

  const pastPuppies = useMemo(
    () =>
      pastLitters.reduce(
        (pups: any[], litter: any) => [
          ...pups,
          ...nameUnnamedPuppies(litter.puppies)
        ],
        []
      ),
    [pastLitters]
  );

  const upcomingLitters = useMemo(
    () =>
      currentLitters.filter(
        (litter: any) => litter.status !== LITTER_STATES.BORN
      ),
    [currentLitters]
  );

  const hasAvailableDogs =
    sortedAvailablePuppies.length > 0 || availableAdultDogs.length > 0;

  return {
    sortedAvailablePuppies,
    availableAdultDogs,
    reservedPuppies,
    pastPuppies,
    upcomingLitters,
    hasAvailableDogs,
    isFetchingLitters: isFetchingCurrentLitters && isFetchingPastLitters,
    isFetchingCurrentLitters
  };
}
